import { useState, type ReactNode } from "react";
import { usePwaInstall } from "../../hooks/usePwaInstall";
import { PwaIOSInstructions } from "./PwaIOSInstructions";
import { PwaManualInstallInstructions } from "./PwaManualInstallInstructions";

interface Props {
  /** Classe do <button> — deixa cada superfície estilizar (pill da landing, linha do settings, etc.). */
  className?: string;
  children: ReactNode;
  /** Chamado após acionar o prompt nativo (ex.: fechar um painel). Não dispara nos fluxos manuais (iOS/manualFallback). */
  onDone?: () => void;
}

type InstructionsModal = "ios" | "manual" | null;

/**
 * Botão de "Instalar app" que se auto-oculta quando o app não é instalável
 * (já instalado, ou navegador sem suporte). No iOS abre as instruções manuais
 * de Compartilhar; em navegadores Chromium sem `beforeinstallprompt` (Brave,
 * Comet) abre instruções de instalação pelo menu; nos demais aciona o prompt
 * nativo do navegador.
 */
export function PwaInstallButton({ className, children, onDone }: Props) {
  const { canInstall, isIOS, manualFallback, promptInstall } = usePwaInstall();
  const [modal, setModal] = useState<InstructionsModal>(null);

  if (!canInstall) return null;

  const handleClick = async () => {
    if (isIOS) {
      setModal("ios");
      return;
    }
    if (manualFallback) {
      setModal("manual");
      return;
    }
    await promptInstall();
    onDone?.();
  };

  return (
    <>
      <button type="button" onClick={handleClick} className={className}>
        {children}
      </button>
      {modal === "ios" && <PwaIOSInstructions onClose={() => setModal(null)} />}
      {modal === "manual" && (
        <PwaManualInstallInstructions onClose={() => setModal(null)} />
      )}
    </>
  );
}
